import React, { useState, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Plus, Trash2, Gift, Calendar, Loader2, CalendarDays, CalendarCheck, CalendarClock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { DatePicker } from '@/components/shared/DatePicker'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { holidayService } from '@/services/holidayService'
import { useAuthStore } from '@/store/authStore'

const holidaySchema = z.object({
  holiday_name: z.string().min(1, 'Please enter holiday name'),
  holiday_date: z.date({ required_error: 'Please select holiday date' }),
})

const Holidays = () => {
  const [loading, setLoading] = useState(false)
  const [deleteHoliday, setDeleteHoliday] = useState(null)
  const addHolidayCardRef = useRef(null)
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  // Check if user can manage holidays
  const canManageHolidays = ['admin', 'hr'].includes(user?.role)

  const form = useForm({
    resolver: zodResolver(holidaySchema),
    defaultValues: {
      holiday_name: '',
      holiday_date: undefined,
    },
  })

  // Fetch holidays
  const { data: holidaysData, isLoading } = useQuery(
    'holidays',
    holidayService.getAllHolidays,
    {
      onError: () => {
        toast.error('Failed to fetch holidays')
      }
    }
  )

  const holidays = holidaysData?.data?.holidays || []

  // Calculate holiday statistics
  const holidayStats = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const upcoming = holidays.filter(h => new Date(h.holiday_date) >= today).length
    const passed = holidays.filter(h => new Date(h.holiday_date) < today).length
    const nextHoliday = holidays
      .filter(h => new Date(h.holiday_date) >= today)
      .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))[0]
    
    let daysUntilNext = null
    if (nextHoliday) {
      const nextDate = new Date(nextHoliday.holiday_date)
      nextDate.setHours(0, 0, 0, 0)
      daysUntilNext = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24))
    }
    
    return { total: holidays.length, upcoming, passed, nextHoliday, daysUntilNext }
  }, [holidays])

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      const formattedValues = {
        ...values,
        holiday_date: dayjs(values.holiday_date).format('YYYY-MM-DD')
      }

      const response = await holidayService.createHoliday(formattedValues)
      
      if (response.success) {
        toast.success('Holiday added successfully!')
        form.reset()
        queryClient.invalidateQueries('holidays')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add holiday')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteHoliday) return
    try {
      const response = await holidayService.deleteHoliday(deleteHoliday.id)
      if (response.success) {
        toast.success(`${deleteHoliday.holiday_name} deleted successfully!`)
        queryClient.invalidateQueries('holidays')
      }
    } catch (error) {
      toast.error('Failed to delete holiday')
    } finally {
      setDeleteHoliday(null)
    }
  }

  const getDaysUntilBadge = (date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const holidayDate = new Date(date)
    holidayDate.setHours(0, 0, 0, 0)
    const diffTime = holidayDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) {
      return <Badge variant="outline" className="border-gray-400 text-gray-500 bg-gray-50">Passed</Badge>
    } else if (diffDays === 0) {
      return <Badge variant="outline" className="border-emerald-500 text-emerald-600 bg-emerald-50">Today!</Badge>
    } else if (diffDays <= 7) {
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600 bg-yellow-50">{diffDays} days</Badge>
    } else if (diffDays <= 30) {
      return <Badge variant="outline" className="border-blue-500 text-blue-600 bg-blue-50">{diffDays} days</Badge>
    } else {
      return <Badge variant="outline" className="border-primary/50 text-primary">{diffDays} days</Badge>
    }
  }

  const scrollToAddForm = () => {
    addHolidayCardRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Company Holidays" 
        description="View and manage company holidays for the year"
        actions={
          canManageHolidays && (
            <Button size="sm" className="h-8 px-3 text-sm" onClick={scrollToAddForm}>
              <Plus className="mr-2 h-3.5 w-3.5" />
              Add Holiday
            </Button>
          )
        }
      />

      {/* Holiday Statistics */}
      <StatCardGroup>
        <StatCard
          title="Total Holidays"
          value={holidayStats.total}
          description="This year"
          icon={CalendarDays}
        />
        <StatCard
          title="Upcoming"
          value={holidayStats.upcoming}
          description="Holidays remaining"
          icon={CalendarClock}
          valueClassName="text-primary"
        />
        <StatCard
          title="Next Holiday"
          value={holidayStats.daysUntilNext !== null ? `${holidayStats.daysUntilNext}d` : '-'}
          description={holidayStats.nextHoliday?.holiday_name || 'No upcoming holidays'}
          icon={CalendarCheck}
          valueClassName="text-emerald-600"
        />
      </StatCardGroup>

      {/* Add Holiday Form - Only for Admin/HR */}
      {canManageHolidays && (
        <Card ref={addHolidayCardRef}>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Add New Holiday</CardTitle>
            <CardDescription>Create a new company holiday</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-wrap gap-4 items-end">
                <FormField
                  control={form.control}
                  name="holiday_name"
                  render={({ field }) => (
                    <FormItem className="flex-1 min-w-[200px]">
                      <FormLabel>Holiday Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Independence Day" {...field} className="h-10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="holiday_date"
                  render={({ field }) => (
                    <FormItem className="w-[200px]">
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={loading} size="sm" className="h-8 px-3 text-sm">
                  {loading ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-3.5 w-3.5" />
                  )}
                  Add Holiday
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* Holidays Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Holiday Calendar</CardTitle>
          <CardDescription>All scheduled company holidays</CardDescription>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-semibold">Holiday Name</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              {canManageHolidays && <TableHead className="text-right font-semibold">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  {canManageHolidays && <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>}
                </TableRow>
              ))
            ) : holidays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManageHolidays ? 4 : 3} className="h-32">
                  <EmptyState
                    icon="calendar"
                    title="No holidays found"
                    description={canManageHolidays ? "Add your first company holiday to get started." : "No company holidays have been added yet."}
                  />
                </TableCell>
              </TableRow>
            ) : (
              holidays
                .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
                .map((holiday) => {
                  const isPassed = new Date(holiday.holiday_date) < new Date(new Date().setHours(0, 0, 0, 0))
                  return (
                    <TableRow key={holiday.id} className={isPassed ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isPassed ? 'bg-gray-100' : 'bg-primary/10'}`}>
                            <Gift className={`h-4 w-4 ${isPassed ? 'text-gray-500' : 'text-primary'}`} />
                          </div>
                          <span className="font-medium">{holiday.holiday_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <p className="font-medium text-sm">
                            {new Date(holiday.holiday_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(holiday.holiday_date).getFullYear()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{getDaysUntilBadge(holiday.holiday_date)}</TableCell>
                      {canManageHolidays && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-sm font-normal text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteHoliday(holiday)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteHoliday} onOpenChange={() => setDeleteHoliday(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Holiday</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteHoliday?.holiday_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Holidays
